/**
 * Shared breadcrumb types and utilities used across inline and page-header variants.
 */

import styles from '../Breadcrumbs.module.scss';

export interface BreadcrumbItem {
  /** Display label */
  label: string;
  /** URL to link to (if undefined, item is current page) */
  href?: string;
}

interface BreadcrumbsBaseProps {
  /** Custom className */
  className?: string;
  /** Test ID for E2E testing */
  testId?: string;
}

export type BreadcrumbsSkeletonComponentProps = BreadcrumbsBaseProps;

export type BreadcrumbAppearance = 'default' | 'cta';

export interface BreadcrumbsInlineProps extends BreadcrumbsBaseProps {
  variant?: 'inline';
  /** Array of breadcrumb items */
  items: BreadcrumbItem[];
  /** When false, skip JSON-LD (e.g. when a parent variant composes the trail). */
  includeJsonLd?: boolean;
  /** Visual style. `cta` matches Gallet hero breadcrumbs (slash separators, green links). */
  appearance?: BreadcrumbAppearance;
}

export interface BreadcrumbsPageHeaderProps extends BreadcrumbsBaseProps {
  variant: 'pageHeader';
  /** Page title rendered below the breadcrumb trail */
  title: string;
  /** Optional breadcrumb trail above the title. Falls back to a home link when omitted. */
  items?: BreadcrumbItem[];
  /** Visual heading size — controls CSS class (h2 = 48px desktop). Semantic tag is always h1. */
  headingType?: 'h1' | 'h2' | 'h3';
  headingClassName?: string;
  /** Home link href used when `items` is omitted */
  homeHref?: string;
  /** Strip all padding (useful when a parent container handles spacing). */
  noPadding?: boolean;
  /** Breadcrumb trail appearance. Use `cta` for Gallet hero headers (slash separators, green links). */
  appearance?: BreadcrumbAppearance;
}

export interface BreadcrumbsSkeletonProps extends BreadcrumbsBaseProps {
  variant: 'skeleton';
}

export type BreadcrumbsProps =
  | BreadcrumbsInlineProps
  | BreadcrumbsPageHeaderProps
  | BreadcrumbsSkeletonProps;

export const ChevronSeparator = () => (
  <svg
    className={styles.chevron}
    width="4.414"
    height="7.414"
    viewBox="0 0 4.414 7.414"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g transform="translate(3.707 0.707) rotate(90)">
      <line
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1"
        transform="translate(3 0)"
        x2="3"
        y2="3"
      />
      <line
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1"
        transform="translate(0 0)"
        x1="3"
        y2="3"
      />
    </g>
  </svg>
);

ChevronSeparator.displayName = 'ChevronSeparator';

export const SlashSeparator = () => (
  <span className={styles.separatorSlash} aria-hidden="true">
    /
  </span>
);

SlashSeparator.displayName = 'SlashSeparator';

export const formatLabel = (str: string): string =>
  str
    .split(' ')
    .map((word) =>
      word === word.toUpperCase()
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');

export const buildBreadcrumbJsonLd = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: formatLabel(item.label),
    item: item.href || undefined,
  })),
});
